import { useQuery } from "@tanstack/react-query";
import React from "react";
import { useNavigate } from "react-router-dom";
import paymentService from "../../service/paymentService";
import AppLayout from "../../lib/components/Layout/app-layout";
import Header from "../../lib/components/Header";
import { Chip, InputAdornment, Tab, Tabs } from "@mui/material";
import moment from "moment";
import PreloaderTable from "../../lib/components/PreloaderTable";
import PaymentsDataTable from "./components/payments-data-table";
import AppDateField from "../../lib/components/AppDateField";
import AppTextField from "../../lib/components/AppTextField";
import { Search } from "@mui/icons-material";
import scrollHandler from "../../utils/scrollHandler";
import useDebounce from "../../hooks/useDebounce";

const tabList = [
  { label: "All", value: "ALL" },
  { label: "Monthly", value: "MONTHLY" },
  { label: "Quarterly", value: "QUARTERLY" },
  { label: "Bi-annually", value: "BIANNUALLY" },
  { label: "Annually", value: "ANNUALLY" },
];

const Payments = () => {
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const page = searchParams.get("page") || "1";
  const limit = searchParams.get("limit") || "20";
  const period = searchParams.get("period") || "";
  const keyword = searchParams.get("keyword") || "";
  const dateFrom = searchParams.get("dateFrom") || null;
  const dateTo = searchParams.get("dateTo") || null;
  const status = searchParams.get("status") || "";
  const searchTerm = useDebounce(keyword);

  const queries = {
    page,
    limit,
    period,
    keyword: searchTerm,
    dateFrom,
    dateTo,
    status,
  };

  const paymentsQuery = useQuery({
    retry: false,
    queryKey: ["payments", ...Object.values(queries)],
    queryFn: () => paymentService.get({ ...queries }),
  });

  const statsQuery = useQuery({
    retry: false,
    queryKey: ["payment-stats", ...Object.values(queries)],
    queryFn: () => paymentService.getInvoicesStats({ ...queries }),
  });

  const { isPending: isLoading, data } = paymentsQuery;
  const { data: stats } = statsQuery;

  const paymentsData = data?.data;
  const statsData = stats?.data?.data;
  return (
    <AppLayout>
      <Header
        title="Payment History"
        description="See all payments made for the Jureb Subscription"
      />
      <div className="p-3 bg-light" style={{ minHeight: "100%" }}>
        <div className="row mb-5">
          <div className="col-6 col-md-3 col-lg-4 mb-3">
            <div>
              <AppTextField
                placeholder="Search payments..."
                value={keyword}
                onChange={(e) => handleChange(e, "keyword")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Search className="text-secondary" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          </div>
          <div className="col-6 col-md-3 col-lg-2 mb-3">
            <div>
              <AppDateField
                label="Date From"
                value={dateFrom}
                onChange={(e) => handleChange(e, "dateFrom")}
              />
            </div>
          </div>
          <div className="col-6 col-md-3 col-lg-2 mb-3">
            <div>
              <AppDateField
                label="Date To"
                value={dateTo}
                minDate={dateFrom}
                onChange={(e) => handleChange(e, "dateTo")}
              />
            </div>
          </div>
        </div>
        <div className="border-bottom mb-5">
          <Tabs value={period || "ALL"} onChange={handleChangeTab}>
            {tabList?.map((item, index) => {
              let value = 0;
              if (statsData) {
                value = statsData[item?.value.toLowerCase()];
              }
              return (
                <Tab
                  key={index}
                  value={item?.value}
                  label={<TabLabel title={item?.label} label={value} />}
                />
              );
            })}
          </Tabs>
        </div>
        <PreloaderTable isLoading={isLoading} />
        <PaymentsDataTable
          isLoading={isLoading}
          data={paymentsData?.data}
          page={Number(page)}
          pageSize={Number(limit)}
          totalPages={paymentsData?.totalPages}
          onPageChange={(value) => handleChange(value, "page")}
          onPageSizeChange={(value) =>
            handleChange({ target: { value } }, "limit")
          }
        />
      </div>
    </AppLayout>
  );

  function handleChangeTab(_, value) {
    searchParams.set("period", value);
    searchParams.set("page", "1");
    navigate(
      {
        pathname: window.location.pathname,
        search: searchParams.toString(),
      },
      { replace: true },
    );
  }

  function handleChange(e, name = "") {
    const value = name.includes("date")
      ? moment(e).format("YYYY-MM-DD")
      : name === "page"
        ? e
        : e.target.value;
    searchParams.set(name, value);
    navigate(`${window.location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
    if (name === "page") scrollHandler();
  }
};

function TabLabel({ title, label }) {
  return (
    <div className="d-flex gap-3 align-items-center">
      <span>{title}</span>
      <Chip label={label} />
    </div>
  );
}

export default Payments;
